/**
 * Miscellaneous Utilities
 * 
 * General utility functions that don't fit into other categories
 */

import { showToast } from '@/shared/utils/sweatAlertCustomHelper'

/**
 * Reset object to default values
 * @param {Object} object - Object to reset
 * @returns {Object} Reset object
 */
export function resetObject(object) {
  Object.keys(object).forEach((key) => {
    const value = object[key];

    if (Array.isArray(value)) {
      object[key] = [];
    } else if (typeof value === "number") {
      object[key] = 0;
    } else if (typeof value === "boolean") {
      object[key] = false;
    } else if (typeof value === "object" && value !== null) {
      object[key] = resetObject(value); // recursive reset
    } else {
      object[key] = "";
    }
  });

  return object;
}

/**
 * Get user location using geolocation API with retry logic
 * Tries high accuracy first, then falls back to low accuracy if needed
 * @returns {Promise<Object>} Promise resolving to {lat, lng} coordinates
 */
export function getUserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    // First attempt: High accuracy with reasonable timeout
    const tryGetLocation = (options, isRetry = false) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          resolve(coords);
        },
        (error) => {
          // If first attempt failed with timeout or position unavailable, try with low accuracy
          if (!isRetry && (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE)) {
            // Retry with low accuracy and longer timeout
            tryGetLocation(
              {
                enableHighAccuracy: false,
                timeout: 15000,
                maximumAge: 60000 // Accept cached position up to 1 minute old
              },
              true // Mark as retry
            );
            return;
          }

          // Handle final error
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "PERMISSION_DENIED";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "POSITION_UNAVAILABLE";
              break;
            case error.TIMEOUT:
              errorMessage = "TIMEOUT";
              break;
            default:
              errorMessage = "UNKNOWN_ERROR";
          }
          
          // Create error object with code for better handling
          const locationError = new Error(errorMessage);
          locationError.code = error.code;
          locationError.originalMessage = error.message;
          reject(locationError);
        },
        options
      );
    };

    // Start with high accuracy
    tryGetLocation({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0 // Don't use cached position for first attempt
    });
  });
}

/**
 * Copy message to clipboard
 * @param {string} content - Content to copy
 */
export function copyMessage(content) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(content);
  } else {
    showToast("Cannot copy in an insecure connection");
  }
}

/**
 * Load link preview using Microlink API
 * @param {string} url - URL to get preview for
 * @returns {Promise<Object|null>} Promise resolving to preview object or null
 */
export async function loadLinkPreview(url) {
  if (!url) return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `https://api.microlink.io?url=${encodeURIComponent(url)}`,
      {
        mode: "cors",
        signal: controller.signal,
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const data = await response.json();
    const meta = data?.data || data;

    if (!meta) return null;

    return {
      title: meta.title || "",
      description: meta.description || "",
      image: meta.image?.url || meta.image || meta.logo?.url || "",
      provider:
        meta.provider ||
        meta.author?.name ||
        new URL(url).hostname.replace(/^www\./, ""),
    };
  } catch (error) {
    return null;
  }
}

